/*****************************************************
Naam: ConfirmPage.jsx
Locatie: /Dashboard/src/pages/ConfirmPage.jsx
Datum: 2026-01-18
Versie: 1.2.0
Auteur: Claude
******************************************************
Beschrijving:
Publieke pagina waar klanten hun opdracht kunnen bevestigen.
Toegankelijk via link in prijsopgave email (geen login nodig).

CHANGELOG v1.2.0 (2026-01-18):
- NIEUW: Validatie en invoer voor ontbrekende adressen
- Klant kan nu ook laad/losadres invullen indien ontbrekend
- needsLaadAdres/needsLosAdres toegevoegd aan checks

CHANGELOG v1.1.0 (2026-01-18):
- Auto-bevestiging: Als alle datums ingevuld zijn, direct bevestigen
- Geen tussenliggende pagina meer als alles compleet is
- Status wordt "Opdracht ontvangen" na bevestiging

Features:
- Toont aanvraag details (route, goederen, prijs)
- Als laaddatum/losdatum ontbreken: invoervelden
- Als laadadres/losadres ontbreken: invoervelden
- Auto-submit als alles compleet is
- Bevestigingsknop
- Foutafhandeling (verlopen link, ongeldige token)
******************************************************/

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const ConfirmPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aanvraagData, setAanvraagData] = useState(null);
  const [laadDatum, setLaadDatum] = useState('');
  const [losDatum, setLosDatum] = useState('');
  // Adres state
  const [laadAdres, setLaadAdres] = useState('');
  const [laadPostcode, setLaadPostcode] = useState('');
  const [laadPlaats, setLaadPlaats] = useState('');
  const [losAdres, setLosAdres] = useState('');
  const [losPostcode, setLosPostcode] = useState('');
  const [losPlaats, setLosPlaats] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);

  useEffect(() => {
    loadAanvraagInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadAanvraagInfo = async () => {
    if (!token) {
      setError('Geen geldige link. Controleer of u de volledige link uit de email heeft gekopieerd.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`/api/confirm/info?token=${encodeURIComponent(token)}`);
      const result = await response.json();

      if (!result.success) {
        if (result.alreadyConfirmed) {
          setAlreadyConfirmed(true);
        }
        setError(result.message || 'Er is een fout opgetreden');
        setLoading(false);
        return;
      }

      setAanvraagData(result.data);
      
      // Pre-fill datums als ze al bestaan
      if (result.data.laadDatum) {
        setLaadDatum(formatDateForInput(result.data.laadDatum));
      }
      if (result.data.losDatum) {
        setLosDatum(formatDateForInput(result.data.losDatum));
      }
      
      // AUTO-SUBMIT: Als alle datums EN adressen al ingevuld zijn, direct bevestigen
      const alleDataCompleet = !result.data.needsLaadDatum && !result.data.needsLosDatum && 
                               !result.data.needsLaadAdres && !result.data.needsLosAdres;
      if (alleDataCompleet) {
        // Direct bevestigen - geen formulier tonen
        await autoSubmitConfirmation(token, result.data);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error loading confirm info:', err);
      setError('Kan aanvraag gegevens niet laden. Probeer het later opnieuw.');
      setLoading(false);
    }
  };

  // Auto-submit functie voor wanneer alle data al compleet is
  const autoSubmitConfirmation = async (confirmToken, data) => {
    try {
      setSubmitting(true);
      
      const response = await fetch('/api/confirm/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: confirmToken,
          laadDatum: null,
          losDatum: null
        })
      });

      const result = await response.json();

      if (!result.success) {
        setError(result.message || 'Er is een fout opgetreden bij het bevestigen');
        setSubmitting(false);
        setLoading(false);
        return;
      }

      // Sla data op voor success scherm
      setAanvraagData(data);
      setSubmitted(true);
      setSubmitting(false);
      setLoading(false);
    } catch (err) {
      console.error('Error auto-submitting confirmation:', err);
      setError('Kan bevestiging niet versturen. Probeer het later opnieuw.');
      setSubmitting(false);
      setLoading(false);
    }
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    // Validatie datums
    if (aanvraagData.needsLaadDatum && !laadDatum) {
      setSubmitError('Vul een laaddatum in');
      return;
    }
    if (aanvraagData.needsLosDatum && !losDatum) {
      setSubmitError('Vul een losdatum in');
      return;
    }
    // Validatie adressen (minimaal adres of plaats)
    if (aanvraagData.needsLaadAdres && !laadAdres && !laadPlaats) {
      setSubmitError('Vul een laadadres of laadplaats in');
      return;
    }
    if (aanvraagData.needsLosAdres && !losAdres && !losPlaats) {
      setSubmitError('Vul een losadres of losplaats in');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch('/api/confirm/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: token,
          laadDatum: laadDatum || null,
          losDatum: losDatum || null,
          laadAdres: laadAdres || null,
          laadPostcode: laadPostcode || null,
          laadPlaats: laadPlaats || null,
          losAdres: losAdres || null,
          losPostcode: losPostcode || null,
          losPlaats: losPlaats || null
        })
      });

      const result = await response.json();

      if (!result.success) {
        setSubmitError(result.message || 'Er is een fout opgetreden');
        setSubmitting(false);
        return;
      }

      setSubmitted(true);
      setSubmitting(false);
    } catch (err) {
      console.error('Error submitting confirmation:', err);
      setSubmitError('Kan bevestiging niet versturen. Probeer het later opnieuw.');
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.loading}>
            <div style={styles.spinner}></div>
            <p>Laden...</p>
          </div>
        </div>
      </div>
    );
  }

  // Already confirmed state
  if (alreadyConfirmed) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <h1 style={styles.logo}>sendwell.</h1>
            <p style={styles.tagline}>get cargo done</p>
          </div>
          <div style={styles.infoBox}>
            <h2 style={styles.infoTitle}>✓ Opdracht Al Bevestigd</h2>
            <p>Deze opdracht is al eerder bevestigd. U hoeft verder niets te doen.</p>
          </div>
          <p style={styles.contactText}>
            Vragen? Neem contact op met SendWell:<br />
            <a href="mailto:info@sendwell.nl" style={styles.link}>info@sendwell.nl</a>
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <h1 style={styles.logo}>sendwell.</h1>
            <p style={styles.tagline}>get cargo done</p>
          </div>
          <div style={styles.errorBox}>
            <h2 style={styles.errorTitle}>⚠️ Fout</h2>
            <p>{error}</p>
          </div>
          <p style={styles.contactText}>
            Neem contact op met SendWell voor assistentie:<br />
            <a href="mailto:info@sendwell.nl" style={styles.link}>info@sendwell.nl</a>
          </p>
        </div>
      </div>
    );
  }

  // Success state (after confirmation)
  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <h1 style={styles.logo}>sendwell.</h1>
            <p style={styles.tagline}>get cargo done</p>
          </div>
          <div style={styles.successBox}>
            <h2 style={styles.successTitle}>✅ Opdracht Bevestigd!</h2>
            <p style={styles.successText}>
              Bedankt voor uw bevestiging.
            </p>
            <p style={styles.successSubtext}>
              Wij gaan direct aan de slag en nemen spoedig contact met u op met de verdere details.
            </p>
          </div>
          <div style={styles.summary}>
            <h3>Samenvatting</h3>
            <p><strong>Aanvraag:</strong> {aanvraagData.aanvraagID}</p>
            <p><strong>Route:</strong> {aanvraagData.laadPlaats} → {aanvraagData.losPlaats}</p>
            <p><strong>Prijs:</strong> € {parseFloat(aanvraagData.prijs).toFixed(2)} (All-In, excl. BTW)</p>
            {laadDatum && <p><strong>Laaddatum:</strong> {formatDateDisplay(laadDatum)}</p>}
            {losDatum && <p><strong>Losdatum:</strong> {formatDateDisplay(losDatum)}</p>}
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.logo}>sendwell.</h1>
          <p style={styles.tagline}>get cargo done</p>
        </div>

        <h2 style={styles.title}>Opdracht Bevestigen</h2>
        
        <div style={styles.klantBadge}>
          {aanvraagData.klantNaam}
        </div>

        {/* Prijs Box */}
        <div style={styles.prijsBox}>
          <h3 style={styles.prijsLabel}>💰 Geoffreerde Prijs</h3>
          <p style={styles.prijsValue}>€ {parseFloat(aanvraagData.prijs).toFixed(2)}</p>
          <p style={styles.prijsNote}>All-In, exclusief BTW</p>
        </div>

        {/* Route Info */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📍 Route</h3>
          <div style={styles.routeBox}>
            <div style={styles.routePoint}>
              <div style={styles.routeLabel}>LADEN</div>
              <div style={styles.routeValue}>
                {aanvraagData.laadPostcode} {aanvraagData.laadPlaats}
              </div>
              <div style={styles.routeCountry}>{aanvraagData.laadLand}</div>
              {!aanvraagData.needsLaadDatum && (
                <div style={styles.routeDate}>{formatDateDisplay(aanvraagData.laadDatum)}</div>
              )}
            </div>
            <div style={styles.routeArrow}>→</div>
            <div style={styles.routePoint}>
              <div style={styles.routeLabel}>LOSSEN</div>
              <div style={styles.routeValue}>
                {aanvraagData.losPostcode} {aanvraagData.losPlaats}
              </div>
              <div style={styles.routeCountry}>{aanvraagData.losLand}</div>
              {!aanvraagData.needsLosDatum && (
                <div style={styles.routeDate}>{formatDateDisplay(aanvraagData.losDatum)}</div>
              )}
            </div>
          </div>
        </div>

        {/* Goederen */}
        {aanvraagData.goederen && aanvraagData.goederen.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>📦 Goederen</h3>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Aantal</th>
                  <th style={styles.th}>Soort</th>
                  <th style={styles.th}>Omschrijving</th>
                  <th style={styles.th}>Afmetingen</th>
                  <th style={styles.th}>Gewicht</th>
                </tr>
              </thead>
              <tbody>
                {aanvraagData.goederen.map((goed, idx) => (
                  <tr key={idx}>
                    <td style={styles.td}>{goed.aantal}</td>
                    <td style={styles.td}>{goed.soort}</td>
                    <td style={styles.td}>{goed.omschrijving || '-'}</td>
                    <td style={styles.td}>
                      {goed.lengte && goed.breedte && goed.hoogte 
                        ? `${goed.lengte}×${goed.breedte}×${goed.hoogte} m`
                        : '-'}
                    </td>
                    <td style={styles.td}>{goed.gewicht ? `${goed.gewicht} kg` : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Datum invoer (indien nodig) */}
        {(aanvraagData.needsLaadDatum || aanvraagData.needsLosDatum) && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>📅 Datums Invullen</h3>
            <p style={styles.dateNote}>
              Vul de ontbrekende datum(s) in om de opdracht te kunnen bevestigen.
            </p>
            
            <div style={styles.dateInputs}>
              {aanvraagData.needsLaadDatum && (
                <div style={styles.dateField}>
                  <label style={styles.dateLabel}>Laaddatum *</label>
                  <input
                    type="date"
                    value={laadDatum}
                    onChange={(e) => setLaadDatum(e.target.value)}
                    style={styles.dateInput}
                    required
                  />
                </div>
              )}
              
              {aanvraagData.needsLosDatum && (
                <div style={styles.dateField}>
                  <label style={styles.dateLabel}>Losdatum *</label>
                  <input
                    type="date"
                    value={losDatum}
                    onChange={(e) => setLosDatum(e.target.value)}
                    style={styles.dateInput}
                    required
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Adres invoer (indien nodig) */}
        {(aanvraagData.needsLaadAdres || aanvraagData.needsLosAdres) && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>📍 Adressen Invullen</h3>
            <p style={styles.dateNote}>
              Vul de ontbrekende adresgegevens in om de opdracht te kunnen bevestigen.
            </p>
            
            {aanvraagData.needsLaadAdres && (
              <div style={styles.adresBlock}>
                <h4 style={styles.adresTitle}>Laadadres</h4>
                <div style={styles.adresFields}>
                  <div style={styles.adresField}>
                    <label style={styles.dateLabel}>Straat + Huisnummer</label>
                    <input
                      type="text"
                      value={laadAdres}
                      onChange={(e) => setLaadAdres(e.target.value)}
                      style={styles.textInput}
                      placeholder="bijv. Industrieweg 15"
                    />
                  </div>
                  <div style={styles.adresFieldSmall}>
                    <label style={styles.dateLabel}>Postcode</label>
                    <input
                      type="text"
                      value={laadPostcode}
                      onChange={(e) => setLaadPostcode(e.target.value)}
                      style={styles.textInput}
                      placeholder="bijv. 1234 AB"
                    />
                  </div>
                  <div style={styles.adresField}>
                    <label style={styles.dateLabel}>Plaats *</label>
                    <input
                      type="text"
                      value={laadPlaats}
                      onChange={(e) => setLaadPlaats(e.target.value)}
                      style={styles.textInput}
                      placeholder="bijv. Amsterdam"
                      required
                    />
                  </div>
                </div>
              </div>
            )}
            
            {aanvraagData.needsLosAdres && (
              <div style={styles.adresBlock}>
                <h4 style={styles.adresTitle}>Losadres</h4>
                <div style={styles.adresFields}>
                  <div style={styles.adresField}>
                    <label style={styles.dateLabel}>Straat + Huisnummer</label>
                    <input
                      type="text"
                      value={losAdres}
                      onChange={(e) => setLosAdres(e.target.value)}
                      style={styles.textInput}
                      placeholder="bijv. Havenstraat 42"
                    />
                  </div>
                  <div style={styles.adresFieldSmall}>
                    <label style={styles.dateLabel}>Postcode</label>
                    <input
                      type="text"
                      value={losPostcode}
                      onChange={(e) => setLosPostcode(e.target.value)}
                      style={styles.textInput}
                      placeholder="bijv. 5678 CD"
                    />
                  </div>
                  <div style={styles.adresField}>
                    <label style={styles.dateLabel}>Plaats *</label>
                    <input
                      type="text"
                      value={losPlaats}
                      onChange={(e) => setLosPlaats(e.target.value)}
                      style={styles.textInput}
                      placeholder="bijv. Rotterdam"
                      required
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bevestig Form */}
        <form onSubmit={handleSubmit}>
          {submitError && (
            <div style={styles.submitError}>
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              ...styles.submitButton,
              opacity: submitting ? 0.7 : 1,
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Bezig met bevestigen...' : '✓ BEVESTIG OPDRACHT'}
          </button>
        </form>

        <p style={styles.disclaimer}>
          Door te bevestigen gaat u akkoord met de geoffreerde prijs en onze algemene voorwaarden.
        </p>
      </div>
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: 'Arial, sans-serif'
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    maxWidth: '700px',
    width: '100%',
    padding: '40px'
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
    paddingBottom: '20px',
    borderBottom: '2px solid #1a5235'
  },
  logo: {
    color: '#1a5235',
    fontSize: '36px',
    fontWeight: 'bold',
    margin: 0
  },
  tagline: {
    color: '#666',
    fontSize: '12px',
    letterSpacing: '3px',
    marginTop: '5px'
  },
  title: {
    textAlign: 'center',
    color: '#333',
    marginBottom: '20px'
  },
  klantBadge: {
    backgroundColor: '#1a5235',
    color: '#fff',
    padding: '10px 20px',
    borderRadius: '5px',
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: '30px'
  },
  prijsBox: {
    backgroundColor: '#f0f8f0',
    border: '3px solid #6cbd6f',
    borderRadius: '10px',
    padding: '25px',
    textAlign: 'center',
    marginBottom: '30px'
  },
  prijsLabel: {
    color: '#1a5235',
    fontSize: '18px',
    marginBottom: '10px'
  },
  prijsValue: {
    color: '#1a5235',
    fontSize: '42px',
    fontWeight: 'bold',
    margin: '10px 0'
  },
  prijsNote: {
    color: '#666',
    fontSize: '14px',
    fontStyle: 'italic'
  },
  section: {
    marginBottom: '30px'
  },
  sectionTitle: {
    color: '#1a5235',
    fontSize: '18px',
    marginBottom: '15px',
    paddingBottom: '10px',
    borderBottom: '1px solid #e0e0e0'
  },
  routeBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f9f9f9',
    padding: '20px',
    borderRadius: '8px'
  },
  routePoint: {
    flex: 1,
    textAlign: 'center'
  },
  routeLabel: {
    fontSize: '12px',
    color: '#666',
    fontWeight: 'bold',
    marginBottom: '5px'
  },
  routeValue: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333'
  },
  routeCountry: {
    fontSize: '14px',
    color: '#666'
  },
  routeDate: {
    fontSize: '14px',
    color: '#1a5235',
    marginTop: '5px'
  },
  routeArrow: {
    fontSize: '30px',
    color: '#1a5235',
    padding: '0 20px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    backgroundColor: '#1a5235',
    color: '#fff',
    padding: '12px',
    textAlign: 'left',
    fontSize: '14px'
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #e0e0e0',
    fontSize: '14px'
  },
  dateNote: {
    color: '#666',
    fontSize: '14px',
    marginBottom: '15px'
  },
  dateInputs: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap'
  },
  dateField: {
    flex: 1,
    minWidth: '200px'
  },
  dateLabel: {
    display: 'block',
    color: '#333',
    fontWeight: 'bold',
    marginBottom: '8px'
  },
  dateInput: {
    width: '100%',
    padding: '12px',
    border: '2px solid #1a5235',
    borderRadius: '8px',
    fontSize: '16px'
  },
  textInput: {
    width: '100%',
    padding: '12px',
    border: '2px solid #1a5235',
    borderRadius: '8px',
    fontSize: '16px',
    boxSizing: 'border-box'
  },
  adresBlock: {
    backgroundColor: '#f9f9f9',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '20px'
  },
  adresTitle: {
    color: '#1a5235',
    fontSize: '16px',
    marginBottom: '15px',
    marginTop: '0'
  },
  adresFields: {
    display: 'flex',
    gap: '15px',
    flexWrap: 'wrap'
  },
  adresField: {
    flex: '2',
    minWidth: '180px'
  },
  adresFieldSmall: {
    flex: '1',
    minWidth: '120px'
  },
  submitButton: {
    width: '100%',
    backgroundColor: '#6cbd6f',
    color: '#fff',
    border: 'none',
    padding: '18px',
    fontSize: '20px',
    fontWeight: 'bold',
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '20px'
  },
  submitError: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '12px',
    borderRadius: '5px',
    marginBottom: '15px',
    textAlign: 'center'
  },
  errorBox: {
    backgroundColor: '#ffebee',
    padding: '30px',
    borderRadius: '8px',
    textAlign: 'center',
    marginBottom: '20px'
  },
  errorTitle: {
    color: '#c62828',
    marginBottom: '10px'
  },
  infoBox: {
    backgroundColor: '#e3f2fd',
    padding: '30px',
    borderRadius: '8px',
    textAlign: 'center',
    marginBottom: '20px'
  },
  infoTitle: {
    color: '#1565c0',
    marginBottom: '10px'
  },
  successBox: {
    backgroundColor: '#e8f5e9',
    padding: '30px',
    borderRadius: '8px',
    textAlign: 'center',
    marginBottom: '20px'
  },
  successTitle: {
    color: '#2e7d32',
    marginBottom: '10px'
  },
  successText: {
    fontSize: '18px',
    color: '#333'
  },
  successSubtext: {
    color: '#666',
    marginTop: '10px'
  },
  summary: {
    backgroundColor: '#f5f5f5',
    padding: '20px',
    borderRadius: '8px'
  },
  disclaimer: {
    fontSize: '12px',
    color: '#999',
    textAlign: 'center',
    marginTop: '20px'
  },
  contactText: {
    textAlign: 'center',
    color: '#666'
  },
  link: {
    color: '#1a5235'
  },
  loading: {
    textAlign: 'center',
    padding: '50px'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #1a5235',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px'
  }
};

export default ConfirmPage;
